import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { waLink } from "@/lib/site";

export function DestinationTile({ dest, size = "normal", index = 0 }) {
    const heightClass =
        size === "tall" ? "md:row-span-2 aspect-[4/5] md:aspect-auto md:min-h-[640px]" :
        size === "wide" ? "md:col-span-8 aspect-[16/9] md:aspect-auto md:min-h-[420px]" :
        "aspect-[4/5]";

    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: index * 0.05 }}
            className={`group relative overflow-hidden border border-white/10 ${heightClass}`}
            data-testid={`destination-tile-${dest.id}`}
        >
            <img
                src={dest.image}
                alt={dest.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
            <div className="absolute inset-0 p-6 lg:p-8 flex flex-col justify-between">
                <div className="flex items-start justify-between">
                    <span className="overline bg-navy/50 backdrop-blur px-3 py-1 border border-white/10">{dest.region}</span>
                    <span className="font-sans text-xs text-white/80 tracking-widest uppercase bg-navy/40 backdrop-blur px-3 py-1 border border-white/10">{dest.tag}</span>
                </div>

                <div>
                    <div className="flex items-end justify-between gap-4">
                        <div>
                            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/60">{dest.country}</p>
                            <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-1 leading-none">{dest.name}</h3>
                        </div>
                        <p className="font-sans text-sm text-gold whitespace-nowrap">{dest.price}</p>
                    </div>

                    <p className="font-sans text-sm text-white/70 mt-4 max-w-md">{dest.description}</p>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <a
                            href={waLink(`Hi! I'd like to book the ${dest.name} experience.`)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`destination-book-${dest.id}`}
                            className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-[#E5C158] px-5 py-2.5 font-sans font-medium uppercase tracking-wider text-[11px] transition-colors"
                        >
                            Book Now <ArrowUpRight size={14} weight="bold" />
                        </a>
                        <Link
                            to="/contact"
                            data-testid={`destination-inquire-${dest.id}`}
                            className="inline-flex items-center gap-2 border border-white/25 hover:border-gold hover:text-gold px-5 py-2.5 font-sans font-medium uppercase tracking-wider text-[11px] transition-colors"
                        >
                            Inquire
                        </Link>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

export default function DestinationsGrid({ destinations, layout = "home" }) {
    if (layout === "home") {
        const [first, second, third, fourth, fifth] = destinations;
        return (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6" data-testid="destinations-grid-home">
                {/* Row 1: wide + normal */}
                <div className="md:col-span-8">
                    <DestinationTile dest={first} size="wide" index={0} />
                </div>
                <div className="md:col-span-4">
                    <DestinationTile dest={second} index={1} />
                </div>
                {/* Row 2: equal thirds */}
                {third && (
                    <div className="md:col-span-4">
                        <DestinationTile dest={third} index={2} />
                    </div>
                )}
                {fourth && (
                    <div className="md:col-span-4">
                        <DestinationTile dest={fourth} index={3} />
                    </div>
                )}
                {fifth && (
                    <div className="md:col-span-4">
                        <DestinationTile dest={fifth} index={4} />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="destinations-grid-all">
            {destinations.map((d, i) => (
                <DestinationTile key={d.id} dest={d} index={i} />
            ))}
        </div>
    );
}
